<script setup>
import { ref, computed } from 'vue'
import { Head, router, usePage } from '@inertiajs/vue3'
import Layout from '@pages/Shared/layouts/StaffLayout.vue'
import Modal from '@pages/staff/components/SectionModal.vue'
import Table from '@pages/Shared/ui/Table.vue'
import Prompt from '@pages/Shared/Prompt.vue'
import { PhBuilding } from "@phosphor-icons/vue"
import { flashToast } from "@/toast.js"

const props = defineProps({
    sections: Object,
})

const openModal       = ref(false)
const modalStatus     = ref(null)
const modalTitle      = ref('')
const showPrompt      = ref(false)
const selectedSection = ref(null)
const deleteId        = ref(null)

const columns = [
    { key: 'index',   label: '#',    type: 'index'   },
    { key: 'name',    label: 'Name'                  },
    { key: 'actions', label: '',     type: 'actions' },
]

const page = usePage()
const can  = computed(() => page.props.can || {})

const handleSearch = (value) => {
    router.get('/staff/manage-sections', { search: value }, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}

const openAddSectionModal = () => {
    selectedSection.value = null
    modalTitle.value      = 'Add Section'
    modalStatus.value     = 'create'
    openModal.value       = true
}

const editSection = (id) => {
    selectedSection.value = props.sections.data.find(s => s.id === id) ?? null
    modalTitle.value      = 'Edit Section'
    modalStatus.value     = 'edit'
    openModal.value       = true
}

const viewSection = (id) => {
    selectedSection.value = props.sections.data.find(s => s.id === id) ?? null
    modalTitle.value      = 'View Section'
    modalStatus.value     = 'view'
    openModal.value       = true
}

const deleteSection = (id) => { deleteId.value = id; showPrompt.value = true }

const confirmDelete = () => {
    router.delete(`/staff/manage-sections/${deleteId.value}`, {
        onSuccess: (page) => { showPrompt.value = false; flashToast(page.props.flash) }
    })
}

const handleSubmit = (form) => {
    const options = {
        onSuccess: (page) => { openModal.value = false; flashToast(page.props.flash) }
    }
    if (modalStatus.value === 'edit') {
        form._method = 'put'
        form.post(`/staff/manage-sections/${form.id}`, options)
    } else {
        form.post('/staff/manage-sections', options)
    }
}
</script>

<template>
    <Head title="Sections Management" />
    <Layout>

        <Prompt
            title="section"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            :status="modalStatus"
            :section="selectedSection"
            @submitted="handleSubmit"
        />

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Sections</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all sections</p>
            </div>
            <button
                v-if="can.createSection"
                @click="openAddSectionModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhBuilding :size="20" />
                Add Section
            </button>
        </div>

        <Table
            :data="sections"
            :columns="columns"
            variant="staff"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            @search="handleSearch"
            @edit="editSection"
            @view="viewSection"
            @delete="deleteSection"
        />

    </Layout>
</template>